import { useState, type ReactNode } from 'react';
import { MEDIA_PLACEHOLDER_GLYPH } from '../../../../ui/workspace/media/glyph.ts';
import {
  ManagedImageProjection,
  refusedImageProjection,
} from '../../../artifacts/images/projection.mjs';
import { ImageFit, ImageRatio } from '../../../artifacts/images/values.mjs';
import type { ManagedImageProjection as ImageProjection } from '../../../artifacts/images/projection.mjs';
import type { AppMediaDeclaration } from '../../../contracts/app-media.mjs';
import { splitDomMetadata, type DomMetadata } from '../../../../ui/workspace/dom-metadata.ts';

export interface ImageCopy {
  capturing: string;
  missing: string;
  unavailable: string;
  refused: string;
  loadFailed: string;
  open(alt: string): string;
  inspect(alt: string): string;
  seen(observedAt: string): string;
}
export interface ImageProps extends DomMetadata {
  /** Current authorized owner projection; never a URL or an object-store reference. */
  image: ImageProjection;
  /** Selected current C1/ART field metadata. Rendering conveys no attachment rights. */
  imageRatio: AppMediaDeclaration['ratio'];
  cropSafe: boolean;
  size?: 'thumb' | 'tile';
  sizes?: string;
  imageFit?: 'contain' | 'cover';
  caption?: string | null;
  copy: ImageCopy;
  /** Presentation state supplied by INS/root; server write admission remains separate. */
  inspectOn?: boolean;
  onOpen?(image: ImageProjection, origin: HTMLButtonElement): void;
}

function placeholderCopy(image: ImageProjection, copy: ImageCopy) {
  switch (image.state) {
    case 'capturing':
      return copy.capturing;
    case 'missing':
      return copy.missing;
    case 'expired':
    case 'withdrawn':
      return copy.unavailable;
    default:
      return copy.refused;
  }
}

function EmptyImage({ label, seen }: { label: string; seen?: string }) {
  return (
    <span className="pc-img-empty" role="img" aria-label={label}>
      <svg
        className="pc-img-glyph"
        viewBox={MEDIA_PLACEHOLDER_GLYPH.viewBox}
        fill={MEDIA_PLACEHOLDER_GLYPH.fill}
        stroke="currentColor"
        strokeWidth={MEDIA_PLACEHOLDER_GLYPH.stroke.width}
        strokeLinecap={MEDIA_PLACEHOLDER_GLYPH.stroke.linecap}
        strokeLinejoin={MEDIA_PLACEHOLDER_GLYPH.stroke.linejoin}
        aria-hidden="true"
      >
        <rect {...MEDIA_PLACEHOLDER_GLYPH.rect} />
        <circle {...MEDIA_PLACEHOLDER_GLYPH.circle} />
        <path d={MEDIA_PLACEHOLDER_GLYPH.path} />
      </svg>
      <span className="pc-img-copy">{label}</span>
      {seen && <span className="pc-img-meta">{seen}</span>}
    </span>
  );
}

function imageVariants(image: ImageProjection & { state: 'ready' }) {
  const widths = new Map<number, string>();
  for (const variant of image.variants) widths.set(variant.width, variant.src);
  return [...widths].sort(([a], [b]) => a - b);
}

function ReadyImage(props: ImageProps & { image: ImageProjection & { state: 'ready' } }) {
  const {
    image,
    imageRatio,
    copy,
    size = 'tile',
    sizes,
    imageFit,
    caption,
    inspectOn,
    onOpen,
  } = props;
  const [bytes, setBytes] = useState<'loading' | 'loaded' | 'failed'>('loading');
  const variants = imageVariants(image);
  const figure = (
    <figure
      {...splitDomMetadata(props).metadata}
      className="pc-img"
      data-state="ready"
      data-ratio={imageRatio}
      data-fit={imageFit}
      data-size={size}
      aria-busy={bytes === 'loading'}
      data-poneva-inspect-kind="image"
      data-poneva-inspect-label={copy.inspect(image.alt)}
    >
      {bytes === 'failed' ? (
        <EmptyImage label={copy.loadFailed} />
      ) : (
        <img
          src={variants[0][1]}
          srcSet={variants.map(([width, src]) => `${src} ${width}w`).join(', ')}
          sizes={sizes ?? (size === 'thumb' ? '48px' : '100vw')}
          width={image.width}
          height={image.height}
          alt={image.alt}
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          onLoad={() => setBytes('loaded')}
          onError={() => setBytes('failed')}
        />
      )}
      {caption && <figcaption className="pc-img-caption">{caption}</figcaption>}
    </figure>
  );
  if (!onOpen || inspectOn || bytes === 'failed') return figure;
  return (
    <button
      type="button"
      className="pc-img-zoom"
      aria-label={copy.open(image.alt)}
      onClick={(event) => onOpen(image, event.currentTarget)}
    >
      {figure}
    </button>
  );
}

/** Browser-safe shared Image. Resolving, capture and the one MediaViewer are host-owned. */
export function Image(props: ImageProps) {
  const parsed = ManagedImageProjection.safeParse(props.image);
  const declaredRatio = ImageRatio.safeParse(props.imageRatio);
  const validPresentation = declaredRatio.success && typeof props.cropSafe === 'boolean';
  const image = parsed.success && validPresentation ? parsed.data : refusedImageProjection();
  const ratio = declaredRatio.success ? declaredRatio.data : '1:1';
  const candidateFit = ImageFit.safeParse(props.imageFit ?? 'contain');
  const fit =
    props.size === 'thumb' || props.cropSafe !== true || !candidateFit.success
      ? 'contain'
      : candidateFit.data;
  if (image.state === 'ready') {
    const key =
      image.imageId +
      ':' +
      image.variants.map((variant) => variant.src + ':' + variant.sha256).join('|');
    return <ReadyImage {...props} key={key} image={image} imageRatio={ratio} imageFit={fit} />;
  }
  const label = placeholderCopy(image, props.copy);
  const seen =
    (image.state === 'expired' || image.state === 'withdrawn') && image.observedAt
      ? props.copy.seen(image.observedAt)
      : undefined;
  return (
    <figure
      {...splitDomMetadata(props).metadata}
      className="pc-img"
      data-state={image.state}
      data-ratio={ratio}
      data-fit={fit}
      data-size={props.size ?? 'tile'}
      data-poneva-inspect-kind="image"
      data-poneva-inspect-label={props.copy.inspect(image.alt)}
    >
      {image.state === 'capturing' ? (
        <span role="img" aria-label={label} />
      ) : (
        <EmptyImage label={label} seen={seen} />
      )}
      {props.caption && <figcaption className="pc-img-caption">{props.caption}</figcaption>}
    </figure>
  );
}

export function ImageGrid({
  children,
  count,
  ratio,
}: {
  children: ReactNode;
  count?: string;
  ratio: AppMediaDeclaration['ratio'];
}) {
  return (
    <>
      {count && (
        <p className="pc-img-count" role="status">
          {count}
        </p>
      )}
      <div className="pc-img-grid" data-ratio={ratio}>
        {children}
      </div>
    </>
  );
}
